import { ContentViewedPageSource, contentViewedPageSource, routes } from '../constants';

// Followed collections (folge-ich) display curated collections the user
// follows, so they attribute as 'curated collections' (SEAR-683 spec).
const routeRootToPageSource: ReadonlyArray<[string, ContentViewedPageSource]> = [
  [routes.dashboard.root, contentViewedPageSource.dashboard],
  [routes.search.root, contentViewedPageSource.search],
  [routes.pinned.root, contentViewedPageSource.favourites],
  [routes.downloads.root, contentViewedPageSource.downloads],
  [routes.collections.root, contentViewedPageSource.myCollections],
  [routes.curatedCollections.root, contentViewedPageSource.curatedCollections],
  [routes.followed.root, contentViewedPageSource.curatedCollections],
  [routes.productPage.root, contentViewedPageSource.product],
];

/**
 * Maps the pathname where a material link is clicked to the Content Viewed
 * `page source` value. Pages outside the tracked list yield undefined — the
 * property is omitted for them.
 */
export const getPageSourceFromPathname = (pathname: string): ContentViewedPageSource | undefined => {
  const match = routeRootToPageSource.find(([root]) =>
    pathname === `/${root}` || pathname.startsWith(`/${root}/`));
  return match?.[1];
};

/**
 * Extracts the page source from navigation state (same-tab navigation).
 */
export const getPageSourceFromState = (state: unknown): ContentViewedPageSource | undefined => {
  return (state as { pageSource?: ContentViewedPageSource })?.pageSource;
};

const isKnownPageSource = (value: string): value is ContentViewedPageSource =>
  (Object.values(contentViewedPageSource) as string[]).includes(value);

/**
 * Extracts the page source from URL query params (new-tab fallback).
 * Validates against the known values — the query param is a system boundary,
 * so an arbitrary ?page_source= must yield undefined, not pollute Amplitude.
 */
export const getPageSourceFromQuery = (query?: { page_source?: string }): ContentViewedPageSource | undefined => {
  const value = query?.page_source;
  return value !== undefined && isKnownPageSource(value) ? value : undefined;
};

/**
 * Location descriptor for switching to a sibling mind item inside the reader.
 * Stamps the `reader` page source in both query and navigation state (the same
 * propagation the card links use) so the resulting Content Viewed is attributed
 * to the reader, instead of losing its source on a bare-path navigation.
 */
export const getReaderSiblingLink = (mindItemId: string) => ({
  pathname: `/${routes.reader.root}/${mindItemId}`,
  query: { page_source: contentViewedPageSource.reader },
  state: { pageSource: contentViewedPageSource.reader },
});
